/**
 * Canasta bot.
 *
 * Plays from its own view only:
 *  - Draw: takes the pile whenever the rules allow it (more eagerly at higher difficulty), otherwise draws.
 *  - Meld: lays down every natural set of three or more, extends partnership melds, spends wild cards only to finish a
 *    canasta or to reach the initial-meld minimum, and always keeps a card to discard.
 *  - Discard: black threes first (they block the pile), then the cheapest natural card, keeping pairs and never
 *    feeding ranks the opponents have already melded; wild cards are kept.
 */
import { parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotEngine } from '../../bots/types.js';
import {
  canastaCardPoints,
  canastaCheckMelds,
  canastaHasCanasta,
  canastaIsWild,
  canastaMeldPoints,
  canastaTakeError,
  type CanastaAction,
  type CanastaLegal,
  type CanastaMeldGroup,
  type CanastaView,
} from './engine.js';

const rankOf = (id: CardId) => parseCard(id).rank;
const pointsOfRank = (r: number) => (r === 14 ? 20 : r >= 8 ? 10 : 5);

/** Natural cards of the hand by rank (4..A), highest points first within nothing special. */
function naturalsByRank(hand: readonly CardId[]): Map<number, CardId[]> {
  const m = new Map<number, CardId[]>();
  for (const c of hand) {
    if (canastaIsWild(c) || rankOf(c) < 4) continue;
    const r = rankOf(c);
    m.set(r, [...(m.get(r) ?? []), c]);
  }
  return m;
}

/** The cards to play from the hand to take the pile, or null when the bot cannot. */
function chooseTake(view: CanastaView): CardId[] | null {
  const top = view.pile.at(-1);
  const team = (view.seat ?? 0) % 2;
  if (top === undefined) return null;
  const rank = rankOf(top);
  const naturals = view.hand.filter((c) => !canastaIsWild(c) && rankOf(c) === rank);
  const wilds = view.hand.filter(canastaIsWild);
  const existing = view.melds[team]!.some((m) => m.rank === rank);
  const tries: CardId[][] = [];
  if (view.pileFrozen) tries.push(naturals);
  else {
    tries.push(naturals);
    if (!existing && naturals.length === 1 && wilds.length > 0) tries.push([...naturals, wilds[0]!]);
    if (existing && naturals.length === 0 && wilds.length > 0) tries.push([wilds[0]!]);
  }
  for (const cards of tries) {
    if (cards.length === 0) continue;
    const err = canastaTakeError({ hand: view.hand, pile: view.pile, frozen: view.pileFrozen, teamMelds: view.melds[team]!, melded: view.melded[team]!, cards });
    if (!err) return cards;
  }
  return null;
}

/** One `meld` action for the current hand, or null when there is nothing worth melding. */
function planMeld(view: CanastaView): CanastaMeldGroup[] | null {
  const seat = view.seat ?? 0;
  const team = seat % 2;
  const teamMelds = view.melds[team]!;
  const melded = view.melded[team]!;
  const minMeld = view.initialMin[team]!;
  const hand = view.hand;
  const byRank = naturalsByRank(hand);
  const wilds = hand.filter(canastaIsWild);
  const groups: CanastaMeldGroup[] = [];
  const wildsLeft = [...wilds];
  const ranks = [...byRank.keys()].sort((a, b) => b - a);

  for (const r of ranks) {
    const cards = byRank.get(r)!;
    const existing = teamMelds.find((m) => m.rank === r);
    if (cards.length >= 3 || existing) groups.push({ rank: r, cards: [...cards] });
  }
  // Wild cards finish canastas.
  for (const m of teamMelds) {
    const g = groups.find((x) => x.rank === m.rank);
    const adding = g ? g.cards.length : 0;
    const naturalCount = m.cards.filter((c) => !canastaIsWild(c)).length + (g?.cards.length ?? 0);
    const wildCount = m.cards.filter(canastaIsWild).length;
    const need = 7 - (m.cards.length + adding);
    if (need > 0 && need <= wildsLeft.length && wildCount + need <= 3 && wildCount + need < naturalCount) {
      const use = wildsLeft.splice(0, need);
      if (g) g.cards.push(...use);
      else groups.push({ rank: m.rank, cards: use });
    }
  }
  if (!melded) {
    let points = groups.reduce((n, g) => n + canastaMeldPoints(g.cards), 0);
    // Pairs plus one wild reach the minimum when the natural sets alone fall short.
    const pairs = ranks.filter((r) => byRank.get(r)!.length === 2).sort((x, y) => pointsOfRank(y) - pointsOfRank(x));
    for (const r of pairs) {
      if (points >= minMeld || wildsLeft.length === 0) break;
      const w = wildsLeft.shift()!;
      const g: CanastaMeldGroup = { rank: r, cards: [...byRank.get(r)!, w] };
      groups.push(g);
      points += canastaMeldPoints(g.cards);
    }
    if (points < minMeld) return null;
  }
  // Drop groups from the end until the engine's own check accepts the action.
  const out = groups.filter((g) => g.cards.length > 0);
  while (out.length > 0) {
    if (!canastaCheckMelds({ hand, groups: out, teamMelds, melded, minMeld })) return out;
    out.pop();
  }
  return null;
}

function discardCost(card: CardId, view: CanastaView): number {
  if (canastaIsWild(card)) return 1000;
  const team = (view.seat ?? 0) % 2;
  const r = rankOf(card);
  if (r === 3) return -80;
  let cost = canastaCardPoints(card);
  if (view.hand.filter((c) => !canastaIsWild(c) && rankOf(c) === r).length >= 2) cost += 60;
  if (view.melds[team]!.some((m) => m.rank === r)) cost += 200;
  if (view.melds[1 - team]!.some((m) => m.rank === r)) cost += 150;
  return cost;
}

function cheapestDiscard(view: CanastaView, cards: readonly CardId[]): CardId {
  return [...cards].sort((a, b) => discardCost(a, view) - discardCost(b, view))[0]!;
}

export const canastaBot: BotEngine<CanastaView, CanastaLegal, CanastaAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'draw') return this.chooseCard!(view, legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind === 'draw') {
      if (legal.canTakePile && !chance(ctx, ctx.profile.mistakeRate * 2)) {
        const cards = chooseTake(view);
        if (cards) return { type: 'takePile', cards };
      }
      return { type: 'draw' };
    }
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    if (!chance(ctx, ctx.profile.mistakeRate * 2)) {
      const groups = planMeld(view);
      if (groups) return { type: 'meld', groups };
    }
    const cards = legal.discard;
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'discard', card: cards[ctx.rng.int(cards.length)]! };
    return { type: 'discard', card: cheapestDiscard(view, cards) };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    const melds = canastaHasCanasta(view.melds[team]!) ? 0.05 : 0;
    return Math.max(0, Math.min(1, 0.5 + diff / 4000 + melds));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'draw') return { type: 'draw' };
    if (legal.kind === 'play') return { type: 'discard', card: cheapestDiscard(view, legal.discard) };
    throw new Error('No legal action');
  },
};
